import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  FaUser,
  FaSearch,
  FaHeart,
  FaShoppingBag,
  FaBars,
  FaSignInAlt,
  FaUserPlus,
  FaSignOutAlt,
  FaBoxOpen,
  FaTachometerAlt,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useMenu } from "../../context/MenuContext";
import CartDrawer from "../cartpage/CartDrawer";
import MegaMenu from "../mens/MegaMenu";
import MegaMenuCricket from "../cricket/MegaMenuCricket";
import MobileNavbar from "./MobileNavbar";
import WishlistDrawer from "../../common/cartpage/WishlistDrawer";
import SearchModal from "./SearchModal";

/* --------------------------------------------------------------------------
   Small building blocks kept in this file so the component stays drop-in.
   -------------------------------------------------------------------------- */

const Badge = ({ count }) =>
  count > 0 ? (
    <span
      className="absolute -top-1.5 -right-2 grid h-[18px] min-w-[18px] place-items-center
                 rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none
                 text-white ring-2 ring-white"
    >
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

const IconButton = ({ label, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`relative grid h-10 w-10 place-items-center rounded-full text-neutral-800
                transition-colors duration-200 hover:bg-neutral-100 hover:text-red-600
                focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600
                focus-visible:ring-offset-2 ${className}`}
  >
    {children}
  </button>
);

const MenuSkeleton = () => (
  <div className="flex items-center gap-8" aria-hidden="true">
    {[64, 48, 72, 56].map((w, i) => (
      <span
        key={i}
        className="h-3 animate-pulse rounded-full bg-neutral-200"
        style={{ width: w }}
      />
    ))}
  </div>
);

/* -------------------------------------------------------------------------- */

const Navbar = () => {
  const [menuData, setMenuData] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [menuError, setMenuError] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(true);

  const { cartCount, wishlistCount, fetchMenuCounts } = useMenu();
  const { token, userName, logout } = useAuth();
  const { pathname } = useLocation();

  // localStorage can hold a raw string; JSON.parse would throw on that.
  const id = useMemo(() => {
    try {
      const raw = localStorage.getItem("authUserId");
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    } catch {
      return null;
    }
  }, [token]);

  const hideTimeoutRef = useRef(null);
  const menuHideTimeoutRef = useRef(null);
  const userMenuRef = useRef(null);

  /* ---------- sliding accent rail under the nav ---------- */
  const navRef = useRef(null);
  const itemRefs = useRef({});
  const [hoverKey, setHoverKey] = useState(null);
  const [rail, setRail] = useState({ left: 0, width: 0, visible: false });

  const activeKey = useMemo(() => {
    if (pathname.startsWith("/newarrival")) return "new-arrivals";
    const match = menuData.find((c) =>
      pathname.startsWith(`/category/${c.slug}`)
    );
    return match ? String(match.id) : null;
  }, [pathname, menuData]);

  const positionRail = useCallback((key) => {
    const el = key ? itemRefs.current[key] : null;
    const nav = navRef.current;
    if (!el || !nav) {
      setRail((r) => ({ ...r, visible: false }));
      return;
    }
    const item = el.getBoundingClientRect();
    const bounds = nav.getBoundingClientRect();
    setRail({ left: item.left - bounds.left, width: item.width, visible: true });
  }, []);

  useEffect(() => {
    positionRail(hoverKey || activeKey);
  }, [hoverKey, activeKey, menuData, scrolled, positionRail]);

  useEffect(() => {
    const onResize = () => positionRail(hoverKey || activeKey);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [hoverKey, activeKey, positionRail]);

  /* ---------- header compacts once the page moves ---------- */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ---------- account dropdown: hover, click, Esc, outside click ---------- */
  const openUserMenu = () => {
    clearTimeout(hideTimeoutRef.current);
    setShowUserDropdown(true);
  };
  const closeUserMenuSoon = () => {
    hideTimeoutRef.current = setTimeout(() => setShowUserDropdown(false), 350);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setShowUserDropdown(false);
      setActiveMenu(null);
    };
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  useEffect(
    () => () => {
      clearTimeout(hideTimeoutRef.current);
      clearTimeout(menuHideTimeoutRef.current);
    },
    []
  );

  const handleMenuMouseEnter = (categoryId) => {
    clearTimeout(menuHideTimeoutRef.current);
    setActiveMenu(categoryId);
  };
  const handleMenuMouseLeave = () => {
    menuHideTimeoutRef.current = setTimeout(() => setActiveMenu(null), 250);
  };

  /* ---------- menu fetch ---------- */
  // Kept out of the dependency array: if MenuContext recreates this function
  // on every render, listing it as a dep re-fires the request in a loop.
  const fetchCountsRef = useRef(fetchMenuCounts);
  useEffect(() => {
    fetchCountsRef.current = fetchMenuCounts;
  }, [fetchMenuCounts]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchMenu = async () => {
      try {
        setLoadingMenu(true);
        setMenuError(null);

        const res = await fetch(
          `https://code.gurusport.in/api/menu${id ? `?id=${id}` : ""}`,
          {
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();

        if (data?.settings?.logo) setLogoUrl(data.settings.logo);
        setMenuData(Array.isArray(data?.data) ? data.data : []);
        fetchCountsRef.current?.(id, token);
      } catch (err) {
        if (err.name !== "AbortError") {
          setMenuError("Menu didn't load. Refresh to try again.");
        }
      } finally {
        setLoadingMenu(false);
      }
    };

    fetchMenu();
    return () => controller.abort();
  }, [id, token]);

  const navLinkClass =
    "relative block py-2 text-[13px] font-semibold uppercase tracking-[0.14em] " +
    "text-neutral-900 transition-colors duration-200 hover:text-red-600 " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 " +
    "focus-visible:ring-offset-4 rounded-sm";

  return (
    <>
      <header className="sticky top-0 z-50">
        {/* Delivery notice — edit or delete this block freely */}
        {noticeOpen && (
          <div className="bg-neutral-900 text-white">
            <div className="mx-auto flex max-w-[1747px] items-center justify-center gap-6 px-4 py-2 text-[11px] tracking-wide">
              <span>Free shipping on prepaid orders</span>
              <span className="hidden h-3 w-px bg-white/25 sm:block" />
              <span className="hidden sm:block">
                Custom team kit ships in 14 days
              </span>
              <button
                type="button"
                onClick={() => setNoticeOpen(false)}
                aria-label="Hide delivery notice"
                className="absolute right-4 text-white/60 transition-colors hover:text-white
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div
          className={`border-b border-neutral-200 bg-white/95 backdrop-blur-md transition-shadow duration-300 ${
            scrolled ? "shadow-[0_8px_30px_-18px_rgba(0,0,0,0.45)]" : ""
          }`}
        >
          <div
            className={`mx-auto flex max-w-[1747px] items-center justify-between px-4 md:px-6
                        transition-[padding] duration-300 motion-reduce:transition-none ${
                          scrolled ? "py-2.5" : "py-4"
                        }`}
          >
            {/* Logo */}
            <Link to="/" className="mr-4 flex shrink-0 items-center md:mr-10">
              <img
                src={logoUrl || "/fallback-logo.png"}
                alt="Guru Sport home"
                className={`w-[115px] transition-all duration-300 motion-reduce:transition-none md:w-40 ${
                  scrolled ? "h-9 md:h-10" : "h-10 md:h-12"
                }`}
              />
            </Link>

            {/* Mobile menu */}
            <div className="lg:hidden">
              <IconButton label="Open menu" onClick={() => setMenuOpen(true)}>
                <FaBars size={20} />
              </IconButton>
            </div>

            {/* Desktop navigation */}
            <nav
              ref={navRef}
              className="relative hidden items-center gap-9 lg:flex"
              onMouseLeave={() => setHoverKey(null)}
            >
              {loadingMenu ? (
                <MenuSkeleton />
              ) : (
                <>
                  <Link
                    to="/newarrival"
                    ref={(el) => (itemRefs.current["new-arrivals"] = el)}
                    onMouseEnter={() => setHoverKey("new-arrivals")}
                    onFocus={() => setHoverKey("new-arrivals")}
                    className={navLinkClass}
                  >
                    New arrivals
                  </Link>

                  {menuData.map((category) => {
                    const subcategories = category.subcategories || [];
                    const key = String(category.id);

                    return (
                      <div
                        key={category.id}
                        className="relative"
                        onMouseEnter={() => {
                          setHoverKey(key);
                          handleMenuMouseEnter(category.id);
                        }}
                        onMouseLeave={handleMenuMouseLeave}
                      >
                        <Link
                          to={`/category/${category.slug}/products`}
                          ref={(el) => (itemRefs.current[key] = el)}
                          onFocus={() => setHoverKey(key)}
                          className={`${navLinkClass} flex items-center gap-1.5`}
                          aria-expanded={
                            subcategories.length > 0
                              ? activeMenu === category.id
                              : undefined
                          }
                        >
                          {category.name}
                          {subcategories.length > 0 && (
                            <span
                              aria-hidden="true"
                              className={`text-[9px] transition-transform duration-200 motion-reduce:transition-none ${
                                activeMenu === category.id ? "rotate-180" : ""
                              }`}
                            >
                              ▼
                            </span>
                          )}
                        </Link>

                        {activeMenu === category.id &&
                          subcategories.length > 0 && (
                            <>
                              {/* keeps hover alive across the gap */}
                              <span className="absolute left-0 top-full h-4 w-full" />
                              {subcategories.length >= 3 ? (
                                <MegaMenu
                                  subcategories={subcategories}
                                  category={category.name}
                                />
                              ) : (
                                <MegaMenuCricket
                                  subcategories={subcategories}
                                  category={category.name}
                                />
                              )}
                            </>
                          )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* the one moving part: an accent rail that follows the cursor */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-1 h-[2px] bg-red-600
                           transition-all duration-300 ease-out motion-reduce:transition-none"
                style={{
                  left: rail.left,
                  width: rail.width,
                  opacity: rail.visible ? 1 : 0,
                }}
              />
            </nav>

            {/* Account, search, wishlist, cart */}
            <div className="relative hidden items-center gap-1 lg:flex">
              <div
                ref={userMenuRef}
                className="relative"
                onMouseEnter={openUserMenu}
                onMouseLeave={closeUserMenuSoon}
              >
                <button
                  type="button"
                  onClick={() => setShowUserDropdown((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={showUserDropdown}
                  className="flex items-center gap-2 rounded-full py-2 pl-3 pr-4 text-neutral-900
                             transition-colors duration-200 hover:bg-neutral-100 hover:text-red-600
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                >
                  <FaUser />
                  <span className="max-w-[110px] truncate text-[13px] font-medium">
                    {token ? userName || "Account" : "Sign in"}
                  </span>
                </button>

                {showUserDropdown && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 mt-2 w-56 rounded-2xl border
                               border-neutral-200 bg-white p-2
                               shadow-[0_24px_60px_-24px_rgba(0,0,0,0.4)]"
                  >
                    {token ? (
                      <>
                        <p className="px-3 pb-2 pt-1 text-xs text-neutral-500">
                          Signed in as{" "}
                          <span className="font-semibold text-neutral-900">
                            {userName || "User"}
                          </span>
                        </p>
                        {[
                          {
                            to: "/userdashboard",
                            icon: <FaTachometerAlt />,
                            label: "Dashboard",
                          },
                          {
                            to: "/userorder",
                            icon: <FaBoxOpen />,
                            label: "Orders",
                          },
                          {
                            to: "/userprofile",
                            icon: <FaUser />,
                            label: "Profile",
                          },
                        ].map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            role="menuitem"
                            onClick={() => setShowUserDropdown(false)}
                            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm
                                       text-neutral-800 transition-colors hover:bg-neutral-100"
                          >
                            <span className="text-neutral-500">{item.icon}</span>
                            {item.label}
                          </Link>
                        ))}
                        <div className="my-1 h-px bg-neutral-200" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setShowUserDropdown(false);
                            logout();
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm
                                     text-red-600 transition-colors hover:bg-red-50"
                        >
                          <FaSignOutAlt /> Sign out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          role="menuitem"
                          onClick={() => setShowUserDropdown(false)}
                          className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm
                                     text-neutral-800 transition-colors hover:bg-neutral-100"
                        >
                          <FaSignInAlt className="text-neutral-500" /> Sign in
                        </Link>
                        <Link
                          to="/signup"
                          role="menuitem"
                          onClick={() => setShowUserDropdown(false)}
                          className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm
                                     text-neutral-800 transition-colors hover:bg-neutral-100"
                        >
                          <FaUserPlus className="text-neutral-500" /> Create
                          account
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              <IconButton label="Search" onClick={() => setSearchOpen(true)}>
                <FaSearch />
              </IconButton>

              {token && (
                <IconButton
                  label="Wishlist"
                  onClick={() => setWishlistOpen(true)}
                >
                  <FaHeart />
                  <Badge count={wishlistCount} />
                </IconButton>
              )}

              <button
                type="button"
                onClick={() => setCartOpen(true)}
                aria-label="Cart"
                title="Cart"
                className="relative ml-1 flex items-center gap-2 rounded-full bg-neutral-900 py-2.5
                           pl-4 pr-5 text-[13px] font-semibold text-white transition-colors
                           duration-200 hover:bg-red-600 focus:outline-none
                           focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2"
              >
                <FaShoppingBag />
                Cart
                <Badge count={cartCount} />
              </button>
            </div>
          </div>

          {menuError && (
            <p className="border-t border-red-100 bg-red-50 px-4 py-2 text-center text-xs text-red-700">
              {menuError}
            </p>
          )}
        </div>
      </header>

      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <WishlistDrawer
        isOpen={wishlistOpen}
        onClose={() => setWishlistOpen(false)}
      />
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />

      <MobileNavbar
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        menuData={menuData}
        loadingMenu={loadingMenu}
        menuError={menuError}
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        wishlistOpen={wishlistOpen}
        setWishlistOpen={setWishlistOpen}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        cartOpen={cartOpen}
        setCartOpen={setCartOpen}
        token={token}
        userName={userName}
        logout={logout}
      />
    </>
  );
};

export default Navbar;